import { isMarchioId, MARCHIO_NEUTRO, MARCHIO_PREDEFINITO, type MarchioId } from './marchi';

/**
 * Brand resolution (ADR-059 §4). The brand must NOT be a data selector a
 * visitor can set from the URL — an installer's fleet is their customer list.
 * So:
 *
 * - on a per-installer host (`{installer}.emacomfort.com`), the brand comes
 *   from the HOST, which is DNS-controlled and unforgeable, and `?brand=` is
 *   INERT;
 * - on an explicit DEMO host (`app`/`app-preprod`, localhost), `?brand=`
 *   survives as a demo override — this is the gated demo the ADR allows;
 * - anywhere else, `?brand=` is inert and the brand falls back to the saved
 *   choice or the default. `?brand=` never selects whose data is shown on a
 *   real host.
 *
 * Pure: the hostname and query string are passed in, so it is fully testable.
 */

export interface RisoluzioneMarchio {
  id: MarchioId;
  /** true when the brand was fixed by the host (an installer host): the UI must not offer a switcher. */
  bloccatoDaHost: boolean;
  /** true only on a demo host: `?brand=` and the in-app brand switcher are allowed. */
  demo: boolean;
}

const HOST_INSTALLATORE = /^([a-z0-9-]+)\.emacomfort\.com$/i;

/** true on a demo host, where `?brand=` is honoured and the in-app brand
 *  switcher is offered. Two different kinds of host qualify — see
 *  `hostVetrina` and `hostSviluppo` — and they differ on whether the app
 *  defaults to real data, which is why the auth gate reads those two and not
 *  this one. */
export function hostDemo(hostname: string): boolean {
  return hostVetrina(hostname) || hostSviluppo(hostname);
}

/**
 * The company's PUBLIC showcase hosts. Anyone may open these, so they are the
 * simulator and nothing else: no sign-in, no real plant, no customer's data
 * one URL away (ADR-059 §4/§5). This never changes.
 */
export function hostVetrina(hostname: string): boolean {
  const h = hostname.toLowerCase();
  return (
    h === 'app.emacomfort.com' ||
    h === 'app-preprod.emacomfort.com' ||
    // Il banco di prova pubblico. Chiunque puo' aprirlo, quindi vale la stessa
    // regola degli altri due: simulatore sempre, accesso mai.
    h === 'demo.emacomfort.com'
  );
}

/**
 * A DEVELOPER's own machine. Not a public surface: whoever opens it is the
 * person running the build, and a build that carries a pool + client id is one
 * pointed at a real backend on purpose. So a configured developer host shows
 * the REAL plant by default and the simulator is asked for explicitly
 * (`/simulatore`), which is the opposite of a showcase host.
 *
 * An unconfigured build (no `.env.local`, and every published demo bundle) has
 * nothing to be real against and stays the simulator regardless.
 */
export function hostSviluppo(hostname: string): boolean {
  const h = hostname.toLowerCase();
  return h === 'localhost' || h === '127.0.0.1' || h.endsWith('.local');
}

/** The installer brand a per-installer host names, if any. */
export function marchioDaHost(hostname: string): MarchioId | null {
  if (hostDemo(hostname)) return null;
  const m = HOST_INSTALLATORE.exec(hostname);
  const label = m?.[1]?.toLowerCase();
  return isMarchioId(label) ? label : null;
}

export function risolviMarchio(
  hostname: string,
  search: string,
  salvato: MarchioId | null,
): RisoluzioneMarchio {
  // 1. A per-installer host fixes the brand and locks it. `?brand=` is inert.
  const daHost = marchioDaHost(hostname);
  if (daHost) {
    return { id: daHost, bloccatoDaHost: true, demo: false };
  }

  // 2. A demo host allows the `?brand=` / `?marchio=` override.
  const demo = hostDemo(hostname);
  if (demo) {
    const params = new URLSearchParams(search);
    const daUrl = params.get('brand') ?? params.get('marchio');
    // 🚨 Su un host VETRINA il ripiego e' il marchio NEUTRO, non quello
    // predefinito. Il predefinito e' `cstai` — un installatore vero — e su un
    // indirizzo pubblico significava presentare a un estraneo un'app marchiata
    // con il nome di un cliente, con sopra un elenco di impianti dall'aria
    // plausibile ("Villa Quercia, Trani — Famiglia Quercia"). I dati sono
    // inventati; il nome dell'installatore no. Chi vuole vedere una marca
    // precisa la chiede con `?brand=`, e la ottiene.
    //
    // Su una macchina di sviluppo il predefinito resta quello di prima: e' la
    // configurazione con cui si lavora tutti i giorni.
    const ripiego = hostVetrina(hostname) ? MARCHIO_NEUTRO : salvato ?? MARCHIO_PREDEFINITO;
    const id = isMarchioId(daUrl) ? daUrl : ripiego;
    return { id, bloccatoDaHost: false, demo: true };
  }

  // 3. Any other host: `?brand=` is inert. Saved choice, else default.
  return { id: salvato ?? MARCHIO_PREDEFINITO, bloccatoDaHost: false, demo: false };
}
